import type { jsPDF } from "jspdf";

export const PDF_INK = [33, 33, 33] as [number, number, number];
export const PDF_BODY = [77, 77, 77] as [number, number, number];
export const PDF_MUTED = [158, 158, 158] as [number, number, number];
export const PDF_ACCENT = [211, 192, 182] as [number, number, number];
export const PDF_LIGHT_BG = [246, 246, 246] as [number, number, number];
export const PDF_RULE = [233, 233, 233] as [number, number, number];

const POPPINS_REGULAR_PATH = "/fonts/poppins-regular.ttf";
const POPPINS_BOLD_PATH = "/fonts/poppins-bold.ttf";

let fontsBase64Cache: { regular: string; bold: string } | null = null;

const fetchAsBase64 = async (path: string): Promise<string> => {
  const res = await fetch(path);
  const arrayBuffer = await res.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const getPoppinsFontsBase64 = async (): Promise<{
  regular: string;
  bold: string;
}> => {
  if (!fontsBase64Cache) {
    fontsBase64Cache = {
      regular: await fetchAsBase64(POPPINS_REGULAR_PATH),
      bold: await fetchAsBase64(POPPINS_BOLD_PATH),
    };
  }
  return fontsBase64Cache;
};

/**
 * Registers the Poppins font on EVERY jsPDF instance. Font data is fetched (and
 * cached) once, but `addFileToVFS`/`addFont` must run on each instance — a
 * previously-cached promise only registered on the first pdf, so later downloads
 * silently fell back to the default font for tables/titles.
 */
export const loadPoppinsFonts = async (pdf: jsPDF): Promise<void> => {
  const { regular, bold } = await getPoppinsFontsBase64();
  const fontName = "Poppins";
  pdf.addFileToVFS("Poppins-Regular.ttf", regular);
  pdf.addFont("Poppins-Regular.ttf", fontName, "normal");
  pdf.addFileToVFS("Poppins-Bold.ttf", bold);
  pdf.addFont("Poppins-Bold.ttf", fontName, "bold");
};

export const drawPdfHeader = (pdf: jsPDF, pageWidth: number): void => {
  pdf.setFillColor(PDF_ACCENT[0], PDF_ACCENT[1], PDF_ACCENT[2]);
  pdf.rect(0, 0, pageWidth, 4, "F");

  pdf.setFont("Poppins", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(PDF_MUTED[0], PDF_MUTED[1], PDF_MUTED[2]);
  pdf.text("ICH NEXT", pageWidth - 40, 26, { align: "right" });
};

export const drawPdfTitle = (
  pdf: jsPDF,
  text: string,
  pageWidth: number,
  y: number,
  underline = true
): void => {
  pdf.setFont("Poppins", "bold");
  pdf.setFontSize(24);
  pdf.setTextColor(PDF_INK[0], PDF_INK[1], PDF_INK[2]);
  pdf.text(text, pageWidth / 2, y, { align: "center" });

  if (underline) {
    pdf.setFillColor(PDF_ACCENT[0], PDF_ACCENT[1], PDF_ACCENT[2]);
    pdf.rect(pageWidth / 2 - 30, y + 6, 60, 2.5, "F");
  }
};

export const drawPdfSectionTitle = (
  pdf: jsPDF,
  text: string,
  pageWidth: number,
  y: number
): void => {
  pdf.setFont("Poppins", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(PDF_BODY[0], PDF_BODY[1], PDF_BODY[2]);
  pdf.text(text, pageWidth / 2, y, { align: "center" });
};

export const drawPdfFooter = (
  pdf: jsPDF,
  pageWidth: number,
  pageHeight: number,
  _pageNum: number
): void => {
  pdf.setDrawColor(PDF_RULE[0], PDF_RULE[1], PDF_RULE[2]);
  pdf.setLineWidth(0.4);
  pdf.line(40, pageHeight - 30, pageWidth - 40, pageHeight - 30);

  pdf.setFont("Poppins", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(PDF_MUTED[0], PDF_MUTED[1], PDF_MUTED[2]);
  pdf.text("ICH Next", 40, pageHeight - 16);
};

/**
 * autoTable `didDrawPage` hook that renders the header band + footer on EVERY
 * page the table draws on (including pages auto-created on overflow). Without
 * this, multi-page tables leave the extra pages with no header/footer.
 */
export const elegantTablePageDecorator =
  (pageWidth: number, pageHeight: number) =>
  (data: { doc: jsPDF; pageNumber: number }) => {
    drawPdfHeader(data.doc, pageWidth);
    drawPdfFooter(data.doc, pageWidth, pageHeight, data.pageNumber);
  };

export const PDF_ELEGANT_TABLE_STYLES = {
  theme: "plain" as const,
  headStyles: {
    fillColor: PDF_ACCENT,
    textColor: [255, 255, 255] as [number, number, number],
    fontStyle: "bold" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0,
    lineColor: PDF_RULE,
    cellPadding: { top: 10, right: 6, bottom: 10, left: 6 },
  },
  bodyStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: PDF_BODY,
    fontStyle: "normal" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0,
    lineColor: PDF_RULE,
  },
  styles: {
    font: "Poppins",
    fontSize: 11,
    cellPadding: { top: 8, right: 6, bottom: 8, left: 6 },
  },
  didParseCell: (data: {
    cell: { styles: Record<string, unknown> };
    section: string;
    row: { index: number };
    column: { index: number };
  }) => {
    if (data.section === "body") {
      // First column: left-aligned, ink, medium weight for scanability
      if (data.column.index === 0) {
        data.cell.styles.halign = "left";
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = PDF_INK;
      }
      // Thin horizontal rule only between body rows (no vertical grid)
      if (data.row.index > 0) {
        data.cell.styles.lineWidth = 0.3;
        data.cell.styles.lineColor = PDF_RULE;
      }
    }
    // Accent underline beneath the header row
    if (data.section === "head") {
      data.cell.styles.lineWidth = 0;
    }
  },
};

/**
 * autoTable style that mirrors the Overall Analysis expanded view table
 * (`OverallAnalysisTable.tsx`): white header with gray-800 bold centered text,
 * gray-700 body text, and a full gray-300 grid on every cell.
 */
export const PDF_OVERALL_ANALYSIS_TABLE_STYLES = {
  theme: "grid" as const,
  headStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [31, 41, 55] as [number, number, number],
    fontStyle: "bold" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
    cellPadding: { top: 9, right: 6, bottom: 9, left: 6 },
  },
  bodyStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [55, 65, 81] as [number, number, number],
    fontStyle: "normal" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
    cellPadding: { top: 7, right: 6, bottom: 7, left: 6 },
  },
  styles: {
    font: "Poppins",
    fontSize: 10,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
  },
};

/**
 * autoTable style that mirrors the Trend Curve expanded view table
 * (`TrendCurveTable.tsx`): white header with gray-800 bold centered text,
 * gray-700 body text, gray-800 medium first column, full gray-300 grid.
 */
export const PDF_TREND_CURVE_TABLE_STYLES = {
  theme: "grid" as const,
  headStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [31, 41, 55] as [number, number, number],
    fontStyle: "bold" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
    cellPadding: { top: 9, right: 6, bottom: 9, left: 6 },
  },
  bodyStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [55, 65, 81] as [number, number, number],
    fontStyle: "normal" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
  },
  styles: {
    font: "Poppins",
    fontSize: 10,
    cellPadding: { top: 7, right: 6, bottom: 7, left: 6 },
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
  },
};

/**
 * autoTable style that mirrors the Color Analysis expanded view table
 * (`ColorAnalysisTable.tsx`): white header with gray-800 bold centered text,
 * gray-700 body text, and a full gray-300 grid on every cell.
 */
export const PDF_COLOR_ANALYSIS_TABLE_STYLES = {
  theme: "grid" as const,
  headStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [31, 41, 55] as [number, number, number],
    fontStyle: "bold" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
    cellPadding: { top: 9, right: 6, bottom: 9, left: 6 },
  },
  bodyStyles: {
    fillColor: [255, 255, 255] as [number, number, number],
    textColor: [55, 65, 81] as [number, number, number],
    fontStyle: "normal" as const,
    halign: "center" as const,
    valign: "middle" as const,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
    cellPadding: { top: 7, right: 6, bottom: 7, left: 6 },
  },
  styles: {
    font: "Poppins",
    fontSize: 10,
    lineWidth: 0.4,
    lineColor: [209, 213, 219] as [number, number, number],
  },
};
